import React from 'react';

export interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  iconFilled: React.ReactNode;
}

/**
 * Разделы кабинета — один источник для нижней панели и для рельса.
 * Раньше оба компонента держали собственные копии этих SVG, и иконки
 * успели разъехаться по толщине штриха.
 *
 * Пара «контур / заливка» на раздел: активный пункт узнаётся формой, а не
 * только цветом пилюли — это единственная подсказка для тех, кто цвет
 * различает плохо.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  {
    label: 'Главная',
    path: '/',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M4 10.4 12 4l8 6.4V19a2 2 0 0 1-2 2h-2.6v-5.6H8.6V21H6a2 2 0 0 1-2-2v-8.6Z"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
      </svg>
    ),
    iconFilled: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M12 3.3 3.5 10a.6.6 0 0 0-.2.5V19a2 2 0 0 0 2 2h4.1v-6.2h5.2V21h4.1a2 2 0 0 0 2-2v-8.5a.6.6 0 0 0-.2-.5L12 3.3Z" />
      </svg>
    ),
  },
  {
    label: 'Каталог',
    path: '/catalog',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden>
        <rect x="3.5" y="3.5" width="17" height="17" rx="4" stroke="currentColor" strokeWidth="1.7" />
        <path d="M3.5 10h17M10 3.5v17" stroke="currentColor" strokeWidth="1.7" />
      </svg>
    ),
    iconFilled: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M3.5 7.5A4 4 0 0 1 7.5 3.5H10V10H3.5V7.5Zm0 4.5H10v8.5H7.5a4 4 0 0 1-4-4V12ZM12 3.5h4.5a4 4 0 0 1 4 4V10H12V3.5Zm0 8.5h8.5v4.5a4 4 0 0 1-4 4H12V12Z" />
      </svg>
    ),
  },
  {
    label: 'Скидки',
    path: '/discounts',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.7" />
        <path d="M9 15.2 15.2 9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        <circle cx="9.4" cy="9.4" r="1.35" fill="currentColor" />
        <circle cx="14.6" cy="14.6" r="1.35" fill="currentColor" />
      </svg>
    ),
    iconFilled: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M12 2.8a9.2 9.2 0 1 0 0 18.4 9.2 9.2 0 0 0 0-18.4ZM9.4 7.6a1.8 1.8 0 1 1 0 3.6 1.8 1.8 0 0 1 0-3.6Zm5.2 5.2a1.8 1.8 0 1 1 0 3.6 1.8 1.8 0 0 1 0-3.6ZM8.2 16.4l8.2-8.2 1.3 1.3-8.2 8.2-1.3-1.3Z" />
      </svg>
    ),
  },
  {
    label: 'Промо',
    path: '/promo',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.7" />
        <path d="M10.2 8.9 15.4 12l-5.2 3.1V8.9Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      </svg>
    ),
    iconFilled: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M12 2.8a9.2 9.2 0 1 0 0 18.4 9.2 9.2 0 0 0 0-18.4Zm-1.6 5.5 6.1 3.7-6.1 3.7V8.3Z" />
      </svg>
    ),
  },
  {
    label: 'QR-коды',
    path: '/qr',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden>
        <rect x="3.5" y="3.5" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <rect x="13.5" y="3.5" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <rect x="3.5" y="13.5" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <path d="M13.5 13.5h3v3h-3v-3Zm4.5 4.5h2.5v2.5H18V18Z" fill="currentColor" />
      </svg>
    ),
    iconFilled: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M3.5 6a2.5 2.5 0 0 1 2.5-2.5h2A2.5 2.5 0 0 1 10.5 6v2A2.5 2.5 0 0 1 8 10.5H6A2.5 2.5 0 0 1 3.5 8V6Zm10 0A2.5 2.5 0 0 1 16 3.5h2A2.5 2.5 0 0 1 20.5 6v2a2.5 2.5 0 0 1-2.5 2.5h-2A2.5 2.5 0 0 1 13.5 8V6Zm-10 10A2.5 2.5 0 0 1 6 13.5h2a2.5 2.5 0 0 1 2.5 2.5v2A2.5 2.5 0 0 1 8 20.5H6A2.5 2.5 0 0 1 3.5 18v-2Zm10-2.5h3v3h-3v-3Zm4.5 4.5h2.5v2.5H18V18Z" />
      </svg>
    ),
  },
];

export const NAV_PATHS: readonly string[] = NAV_ITEMS.map((item) => item.path);

export function getNavItems(t: {
  nav: {
    home: string;
    catalog: string;
    discounts: string;
    promo: string;
    qr: string;
  };
}): readonly NavItem[] {
  return [
    { ...NAV_ITEMS[0], label: t.nav.home },
    { ...NAV_ITEMS[1], label: t.nav.catalog },
    { ...NAV_ITEMS[2], label: t.nav.discounts },
    { ...NAV_ITEMS[3], label: t.nav.promo },
    { ...NAV_ITEMS[4], label: t.nav.qr },
  ];
}
